import {ArrowRight, Check, ExpandMore, MoreVert, North, Search, Sort, South, ViewColumnOutlined} from "@mui/icons-material";
import {Avatar, Button, Checkbox, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Tooltip, Typography} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import {DataGridPro} from "@mui/x-data-grid-pro";
import {LicenseInfo} from "@mui/x-license";
import React, {Component} from "react";
import {withAppContext} from "../util/withAppContext";
import {Black, Blue3, Grey4, Grey5} from "../theme/styles";
import {prepareBuffer} from "../util/Buffer";
import {formatLocal, formatLocalDate} from "../util/Formatters";
import css_cres_table from "./Common/css/PlatformTable.module.scss";
import css_button from "./Common/css/CCButton.module.scss";
import withBreakpoint from "../util/withBreakpoint";
import MemoryContext from "../util/MemoryContext";
import EnterpriseComponent from "../eoec/ec/EnterpriseComponent";
import {makeEnterpriseComponent} from "../util/utility";
import SimpleSearchFormApplet from "../eoec/applet/impl/SimpleSearchFormApplet";
import EventBus from "../util/EventBus";
import spinner from "../assets/Spinner-1s-200px.svg";

LicenseInfo.setLicenseKey(
    "5ff04d2f76e0640d4c269d1965328415Tz0xMjcxMDksRT0xODA1NDE0Mzk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMy0yMDI0LEtWPTI="
);

export class PlatformTable2 extends Component {
    state = {
        fetchBackgroundOperation: false,
        buffer: [],
        previousBuffer: [],
        showRowsToDisplayButtons: false,
        showColumnSelection: false,
        columnSelection: {},
        // User-chosen column visibility: {field: false} hides a column. Only OPTIONAL columns
        // (metadata hideable !== false) are ever hidden; required + chrome columns stay visible.
        // Seeded from sessionStorage (per applet) in the constructor; toggled via the Columns menu.
        columnVisibilityModel: {},
        columnMenuTarget: null,
        showMenu: false,
        sortMenuTarget: null,
        searchMenuTarget: null,
        menuTarget: null,
        showSearch: false,
        sortSpecification: null,
        showSortSelection: false,
        activeSort: null,
        queryController: this,
    };

    constructor(props) {
        super(props);

        this.applet = props.applet;

        this.searchSpecification = null;

        this.sortSpecification = {Options: []};
        this.sortSelections = null;

        this.searchEnterpriseComponent = makeEnterpriseComponent(this.applet.getEnterpriseComponent().getName());
        this.searchEnterpriseComponent.companionEnterpriseObject = this.applet.getEnterpriseComponent().getEnterpriseObject();

        MemoryContext.components[`PlatformTable-${this.getName()}`] = {}
        this.memoryState = MemoryContext.components[`PlatformTable-${this.getName()}`];
        if (this.memoryState.pageSize === undefined) this.memoryState.pageSize = this.props.pageSize?.default ?? 5;
        if (this.memoryState.numberOfPages === undefined) this.memoryState.numberOfPages = 1;
        if (this.memoryState.pageToDisplay === undefined) this.memoryState.pageToDisplay = 1;

        this.state.columnVisibilityModel = this.loadColumnVisibility();
    }

    // ---- Column visibility (show/hide columns) -------------------------------------------------
    // Required columns = metadata hideable === false; chrome columns = no headerTitle. Neither can
    // be hidden. Everything else is optional and user-toggleable via the Columns menu; the choice
    // is persisted per applet in sessionStorage (as the list of hidden field ids).

    columnVisibilityStorageKey = () => `platformTable.cols.${this.getName()}`;

    // Default visibility from column metadata: an OPTIONAL column with `show === false` starts hidden
    // (still available in the Columns menu). Required (hideable === false) and chrome columns (no
    // headerTitle) always show. Used only when the user has made no explicit choice yet.
    defaultColumnVisibility = () => {
        const model = {};
        (this.props.columns || []).forEach((col) => {
            if (col.hideable === false || !col.headerTitle) return;
            if (col.show === false) model[col.id] = false;
        });
        return model;
    };

    loadColumnVisibility = () => {
        try {
            const raw = sessionStorage.getItem(this.columnVisibilityStorageKey());
            if (raw !== null) {
                // The user has customized this table before — their saved choice is authoritative.
                const hidden = JSON.parse(raw);
                const model = {};
                if (Array.isArray(hidden)) hidden.forEach((field) => { model[field] = false; });
                return this.sanitizeColumnVisibility(model);
            }
        } catch (e) { /* fall through to metadata defaults */ }
        return this.defaultColumnVisibility();
    };

    persistColumnVisibility = (model) => {
        try {
            const hidden = Object.keys(model).filter((field) => model[field] === false);
            sessionStorage.setItem(this.columnVisibilityStorageKey(), JSON.stringify(hidden));
        } catch (e) { /* sessionStorage unavailable — keep in-memory only */ }
    };

    sanitizeColumnVisibility = (model) => {
        const safe = {...model};
        (this.props.columns || []).forEach((col) => {
            if (col.hideable === false || !col.headerTitle) delete safe[col.id];
        });
        return safe;
    };

    onColumnVisibilityModelChange = (model) => {
        const safe = this.sanitizeColumnVisibility(model);
        this.persistColumnVisibility(safe);
        this.setState({columnVisibilityModel: safe});
    };

    setColumnVisibility = (field, visible) => {
        const model = {...this.state.columnVisibilityModel};
        if (visible) delete model[field]; else model[field] = false;
        this.onColumnVisibilityModelChange(model);
    };

    componentDidMount() {

        EventBus.components.addListener(this.searchEnterpriseComponent.serialNumber + "-form-applet-cancel", this.onSearchCancelHandler);
        EventBus.components.addListener(this.searchEnterpriseComponent.serialNumber + "-form-applet-reset", this.onSearchResetHandler);

        this.applet.getEnterpriseComponent().addRecordChangedListener(this);
        this.applet.getEnterpriseComponent().addNewQueryRecordListener(this);
        this.applet.getEnterpriseComponent().addLoadStartedListener(this);
        this.applet.getEnterpriseComponent().addLoadCompletedListener(this);
        this.applet.getEnterpriseComponent().addValueChangedListener(this);
        this.applet.getEnterpriseComponent().addInvalidListener(this);
        this.applet.getEnterpriseComponent().addUpdateStartedListener(this);
        this.applet.getEnterpriseComponent().addUpdateCompletedListener(this);
        this.applet.getEnterpriseComponent().addDeleteCompletedListener(this);
        this.applet.getEnterpriseComponent().addInsertStartedListener(this);
        this.applet.getEnterpriseComponent().addInsertCompletedListener(this);
        this.applet.getEnterpriseComponent().addInsertRollbackCompletedListener(this);
        this.applet.getEnterpriseComponent().addQueryRollbackCompletedListener(this);
        this.applet.getEnterpriseComponent().addSingleRecordRefreshStartedListener(this);
        this.applet.getEnterpriseComponent().addSingleRecordRefreshCompletedListener(this);

        this.searchEnterpriseComponent.addLoadStartedListener(this);
        this.searchEnterpriseComponent.addLoadCompletedListener(this);
        this.searchEnterpriseComponent.addInvalidListener(this);

        this.makeSortSpecification(null);

        if (this.state.action === "New") {
            this.applet.getEnterpriseComponent().newRecord();
        } else {
            this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
            if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
            this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility()?.Mode);
            this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility()?.Value);
            this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
        }
    }

    /** Keep the chosen sort with the page and page size, which already survive navigation.
     *
     * memoryState is MemoryContext.components[`PlatformTable-<name>`] -- per table, and outliving
     * the component. Without this, opening a record and coming back rebuilt the table from props
     * and silently reverted to the applet's default sort, throwing away a choice the user had made
     * two clicks earlier. Reported by Ed.
     */
    rememberSort = (sort) => {
        if (!sort || sort.Identifier === undefined) return;
        this.memoryState.sortIdentifier = sort.Identifier;
        this.memoryState.sortDirections = (sort.Options ?? []).reduce((out, option) => {
            out[option.SortAttribute] = option.Direction;
            return out;
        }, {});
    };

    /** The remembered selection out of a freshly built list, or undefined if there is none.
     *
     * Matched by Identifier rather than kept by reference: the selections are rebuilt from props on
     * every mount, so the remembered object is not one of them. A remembered sort that no longer
     * exists -- an applet whose sort list changed between releases -- simply does not match, and
     * the default applies.
     */
    rememberedSort = (sortSelections) => {
        const remembered = sortSelections.find(s => s.Identifier === this.memoryState.sortIdentifier);
        if (!remembered) return undefined;
        // The direction is remembered too, so a flipped column stays flipped.
        for (const option of remembered.Options ?? []) {
            const direction = this.memoryState.sortDirections?.[option.SortAttribute];
            if (direction) option.Direction = direction;
        }
        return remembered;
    };

    makeSortSpecification = (sortModel) => {
        let sortSelections = [];
        let sortSpecification = {};
        let sortSelection = {};
        sortSelection["Identifier"] = "Row Identity";
        sortSelection["Options"] = [{
            "DisplayName": "Creation Datetime",
            "Direction": "DESC",
            "SortAttribute": "RowIdentity",
        }];
        // NOT OFFERED IN THE MENU. This is the framework's fallback ordering, kept so a grid that
        // declares no sorts of its own still has one -- the branch below already makes it the
        // invisible default in exactly that case. Every other grid declares its own, and showing
        // this alongside them put a second date entry in the menu: "Creation Datetime" sitting next
        // to the grid's own "Created", reading as a duplicate of it.
        //
        // WORSE THAN A DUPLICATE, because it does not sort. RowIdentity is not an attribute most
        // IDOCs expose -- v1.ObjectRevision-001, which backs every Foundation list, has no such tag
        // -- so choosing it asked the server to order by a column that is not in the payload.
        // Reported by Ed against the Purchase Order table.
        sortSelection["Visible"] = false;
        sortSelection["Default"] = false;
        sortSelections.push(sortSelection);

        if (this.props.sort !== undefined && this.props.sort !== null) {
            for (let i = 0; i < this.props.sort.length; i++) {
                sortSelection = {};
                sortSelection["Identifier"] = this.props.sort[i].Identifier;
                sortSelection["Visible"] = this.props.sort[i].Visible;
                sortSelection["Options"] = this.props.sort[i].Options;
                sortSelection["Default"] = this.props.sort[i].Default === true;
                sortSelections.push(sortSelection);
            }
        }

        if (sortModel !== null) {
            if (sortModel.length === 0) {
                sortSpecification = this.sortSpecification;
                if (sortSpecification["Options"][0]["Direction"] === "desc") {
                    sortSpecification["Options"][0]["Direction"] = "asc";
                } else {
                    sortSpecification["Options"][0]["Direction"] = "desc";
                }
                sortSelections = this.sortSelections;
            } else {
                for (let i = 0; i < sortModel.length; i++) {
                    for (let j = 0; j < sortSelections.length; j++) {
                        for (let t = 0; t < sortSelections[j]["Options"].length; t++) {
                            if (sortSelections[j].Options[t]["SortAttribute"] === sortModel[i]["field"]) {
                                sortSelections[j].Options[t]["Direction"] = sortModel[i]["sort"];
                            }
                        }
                    }
                }
            }
        }

        if (sortSelections.length === 1) {
            sortSelections[0].Default = true;
            sortSelections[0].Visible = false;
        }

        // THE REMEMBERED SORT WINS OVER THE APPLET'S DEFAULT. Default is what a table opens with the
        // first time; it is not what it should revert to after the user has chosen something else
        // and merely visited a record.
        const remembered = this.rememberedSort(sortSelections);
        if (remembered) {
            sortSpecification["Identifier"] = remembered.Identifier;
            sortSpecification["Visible"] = remembered.Visible;
            sortSpecification["Options"] = remembered.Options;
        } else {
            for (let i = 0; i < sortSelections.length; i++) {
                if (sortSelections[i].Default === true) {
                    sortSpecification["Identifier"] = sortSelections[i].Identifier;
                    sortSpecification["Visible"] = sortSelections[i].Visible;
                    sortSpecification["Options"] = sortSelections[i].Options;
                }
            }
        }

        this.setSortSelections(sortSelections);
        this.setSortSpecification(sortSpecification);
        this.setState({sortSpecification: sortSpecification, activeSort: sortSpecification, mode: this.applet.mode});
    };

    componentWillUnmount() {
        this.applet.getEnterpriseComponent().removeAllListeners(this);
        this.searchEnterpriseComponent.removeAllListeners(this);

        EventBus.components.removeListener(this.searchEnterpriseComponent.serialNumber + "-form-applet-cancel", this.onSearchCancelHandler);
        EventBus.components.removeListener(this.searchEnterpriseComponent.serialNumber + "-form-applet-reset", this.onSearchResetHandler);
    }

    doneColumnSelectionHandler() {
        this.setState({showColumnSelection: false});
    };

    doneSortSelectionHandler() {
        this.setState({showSortSelection: false});
    };

    enterpriseComponentInsertStarted(ec) {

        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: true});
    };

    enterpriseComponentInsertCompleted(ec){
        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});

        ec.invalidate();
    };

    enterpriseComponentDeleteCompleted(ec) {
        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() - 1 ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});

        ec.invalidate();
    };

    enterpriseComponentInvalidated(ec) {
        this.memoryState.pageToDisplay = 1;

        this.setState({buffer: prepareBuffer(ec)});

        this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
        if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
        this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility().Mode);
        this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility().Value);

        this.applet.getEnterpriseComponent().executeQuery(1, this.memoryState.pageSize);
    };

    enterpriseComponentInsertRollbackCompleted(ec) {
        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});
    };

    enterpriseComponentLoadStarted(ec) {
        this.setState({fetchBackgroundOperation: true, previousBuffer: this.state.buffer});
    };

    enterpriseComponentLoadCompleted(ec) {
        if (ec.serialNumber === this.applet.getEnterpriseComponent().serialNumber) {
            this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
            this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

            this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false, queryController: ec});
        }
        if (ec.serialNumber === this.searchEnterpriseComponent.serialNumber) {
            this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.searchEnterpriseComponent.getTotalRowcount() ?? 0) / this.memoryState.pageSize));
            this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

            this.applet.getEnterpriseComponent().resultsBuffer = this.searchEnterpriseComponent.resultsBuffer;
            this.applet.getEnterpriseComponent().totalRowcount = this.searchEnterpriseComponent.totalRowcount;
            this.applet.getEnterpriseComponent().setMode(EnterpriseComponent.MODE_RESULTS);
            this.applet.getEnterpriseComponent().setIsValid(true);

            this.applet.getEnterpriseComponent().invalidateChildren();

            let queryBuffer = this.searchEnterpriseComponent.queryBuffer;
            this.searchEnterpriseComponent.newQuery();
            this.searchEnterpriseComponent.queryBuffer = queryBuffer;
            this.applet.getEnterpriseComponent().setPagePointer(this.memoryState.pageToDisplay);
            this.setState({buffer: prepareBuffer(this.applet.getEnterpriseComponent()), fetchBackgroundOperation: false, queryController: ec});
        }
    };

    enterpriseComponentValueChanged(ec) {
        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});
    };

    enterpriseComponentNewQueryRecordStarted(ec) {
       this.setState({buffer: prepareBuffer(ec)});
    };

    enterpriseComponentQueryRollbackCompleted(ec) {
        this.setState({buffer: prepareBuffer(ec)});

        this.applet.getEnterpriseComponent().executeQuery(1, this.memoryState.pageSize);
        // this.setSearchSpecification(null);
        // this.applet.getEnterpriseComponent().invalidate();
        // this.setState({showSearch: false, searchMenuTarget: null});
    };

    enterpriseComponentRecordChanged(ec){
        this.setState({buffer: prepareBuffer(ec)});
    };

    enterpriseComponentUpdateStarted(ec) {
    };

    enterpriseComponentUpdateCompleted(ec){

        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        this.setState({buffer: prepareBuffer(ec), fetchBackgroundOperation: false});
    };

    enterpriseComponentSingleRecordRefreshStarted(ec) {
    };

    enterpriseComponentSingleRecordRefreshCompleted(ec) {

        this.memoryState.numberOfPages = Math.max(1, Math.ceil((this.applet.getEnterpriseComponent().getTotalRowcount() ?? 0) / this.memoryState.pageSize));
        this.memoryState.pageToDisplay = Math.min(this.memoryState.pageToDisplay, this.memoryState.numberOfPages);

        let buffer = prepareBuffer(ec);
        this.setState({buffer: buffer});
    };

    getCellRenderer(name) {
        if (name === "Row Selection Indicator Cell") {
            return this.renderRowSelectionIndicatorCell;
        } else if (name === "Standard Single Cell") {
            return this.renderStandardSingleCell;
        } else if (name === "Account User Name Cell") {
            return this.renderAccountUserNameCell;
        } else if (name === "Local Date Cell") {
            return this.renderLocalDateCell;
        } else if (name === "Local Datetime Cell") {
            return this.renderLocalDatetimeCell;
        } else if (name === "Timing Cell") {
            return this.renderTimingCell;
        } else if (name === "Edit Cell") {
            return this.renderEditCell;
        }

        console.log("Unknown cell renderer \"" + name + "\"");
        return null;
    }

    _getColumns = (breakpoint, loading) => {
        let columns = [];

        for (let columnIndex = 0; columnIndex < this.props.placement[breakpoint].length; columnIndex++) {
            let columnMetadata = this.props.columns.find((element) => element.id === this.props.placement[breakpoint][columnIndex].id);
            let headerRenderer = columnMetadata.headerRenderer;

            let sortable = false;
            if (this.getSortSpecification() !== null && this.getSortSpecification().Options !== undefined) {
                for (let i = 0; i < this.getSortSpecification().Options.length; i++) {
                    if (columnMetadata.id === this.getSortSpecification().Options[i].SortAttribute) {
                        sortable = true;
                    }
                }
            }
            let headerAlign = columnMetadata.headerAlign;
            if (headerAlign === undefined) {
                headerAlign = "Left"
            }
            let column = {
                field: columnMetadata.id,
                headerName: columnMetadata.headerTitle,
                editable: false,
                sortable: false,
                filterable: false,
                headerAlign: headerAlign,
                resizable: columnMetadata.resizable,
                pinnable: false,
                disableColumnMenu: true,
                hideable: columnMetadata.hideable
            };
            if (columnMetadata.flex !== undefined) {
                column["flex"] = columnMetadata.flex;
            } else if (columnMetadata.width !== undefined) {
                column["width"] = columnMetadata.width + (sortable ? 45 : 0);
            }
            if (loading) {
                if (this.state.previousBuffer.length === 0) {
                    column["renderCell"] = null;
                } else {
                    column["renderCell"] = (params) => this.getCellRenderer(columnMetadata.cellRenderer)(this, params, this.props.placement[breakpoint][columnIndex].id, breakpoint);
                }
            } else {
                if (this.state.buffer.length === 0) {
                    column["renderCell"] = null;
                } else {
                    column["renderCell"] = (params) => this.getCellRenderer(columnMetadata.cellRenderer)(this, params, this.props.placement[breakpoint][columnIndex].id, breakpoint);
                }
            }
            if (headerRenderer === null) {
                column["renderHeader"] = null;
            } else {
                column["renderHeader"] = (params) => this.getHeaderRenderer(columnMetadata.headerRenderer)(this, params, this.props.placement[breakpoint][columnIndex].id, breakpoint);
            }
            columns.push(column);
        }
        return columns;
    };

    getColumns = (breakpoint, loading) => {
        if (breakpoint === undefined) {
            breakpoint = "xl";
        }

        let columns = [];

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumns("sm", loading);
            } else {
                columns = this._getColumns(breakpoint, loading);
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumns("md", loading);
            } else {
                columns = this._getColumns(breakpoint, loading);
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumns("lg", loading);
            } else {
                columns = this._getColumns(breakpoint, loading);
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumns("xl", loading);
            } else {
                columns = this._getColumns(breakpoint, loading);
            }
        } else if (breakpoint === "xl") {
            columns = this._getColumns(breakpoint, loading);
        }

        return columns;
    };

    getColumnCount = (breakpoint) => {
        let columnCount = 0;

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumnCount("sm");
            } else {
                columnCount = this.props.placement["xs"].length;
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumnCount("md");
            } else {
                columnCount = this.props.placement["sm"].length;
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumnCount("lg");
            } else {
                columnCount = this.props.placement["md"].length;
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getColumnCount("xl");
            } else {
                columnCount = this.props.placement["lg"].length;
            }
        } else if (breakpoint === "xl") {
            columnCount = this.props.placement["xl"].length;
        }

        return columnCount;
    };

    getHeaderRenderer = (name) => {
        if (name === "Standard Single Header") {
            return this.renderStandardSingleHeader;
        } else if (name === "") {
            return this.renderEmptyHeader
        }

        return null;

    };

    _getHeaderCells = (breakpoint) => {
        let cells = [];

        for (let columnIndex = 0; columnIndex < this.props.placement[breakpoint].length; columnIndex++) {
            let headerRenderer = this.props.columns.find((element) => element.id === this.props.placement[breakpoint][columnIndex].id).headerRenderer;

            if (headerRenderer === null) console.log("Header renderer not found");
            else {
                cells.push(this.getHeaderRenderer(headerRenderer)(this.props.placement[breakpoint][columnIndex].id));
            }
        }

        return cells;
    };

    getHeaderCells = (breakpoint) => {
        let cells = [];

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getHeaderCells("sm");
            } else {
                cells = this._getHeaderCells(breakpoint);
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getHeaderCells("md");
            } else {
                cells = this._getHeaderCells(breakpoint);
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getHeaderCells("lg");
            } else {
                cells = this._getHeaderCells(breakpoint);
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getHeaderCells("xl");
            } else {
                cells = this._getHeaderCells(breakpoint);
            }
        } else if (breakpoint === "xl") {
            cells = this._getHeaderCells(breakpoint);
        }

        return cells;
    };

    getName = () => {
        return this.applet.getName();
    };

    _getPinnedColumns = (breakpoint) => {
        let leftPinnableColumns = [];
        let rightPinnableColumns = [];

        for (let columnIndex = 0; columnIndex < this.props.placement[breakpoint].length; columnIndex++) {
            let columnMetadata = this.props.columns.find((element) => element.id === this.props.placement[breakpoint][columnIndex].id);
            if (columnMetadata.pinned === undefined || columnMetadata.pinned === false) {
                continue;
            }
            let headerRenderer = columnMetadata.headerRenderer;

            if (headerRenderer === null) console.log("Header renderer not found");
            else {
                let sortable = false;
                for (let i = 0; i < this.getSortSpecification().Options.length; i++) {
                    if (columnMetadata.id === this.getSortSpecification().Options[i].SortAttribute) {
                        sortable = true;
                    }
                }
                let column = {
                    field: columnMetadata.id,
                    headerName: columnMetadata.headerTitle,
                    editable: false,
                    sortable: sortable,
                    filterable: columnMetadata.filterable,
                    headerAlign: columnMetadata.align,
                    resizable: columnMetadata.resizable,
                    hideable: columnMetadata.hideable,
                };
                if (columnMetadata.flex !== undefined) {
                    column["flex"] = columnMetadata.flex;
                } else if (columnMetadata.width !== undefined) {
                    column["width"] = columnMetadata.width;
                }
                if (this.state.buffer.length === 0) {
                    column["renderCell"] = null;
                } else {
                    column["renderCell"] = (params) => this.getCellRenderer(columnMetadata.cellRenderer)(this, params, this.props.placement[breakpoint][columnIndex].id, breakpoint);
                }
                if (columnMetadata.pinned.toLowerCase() === "right") {
                    rightPinnableColumns.push(column);
                } else if (columnMetadata.pinned.toLowerCase() === "left") {
                    leftPinnableColumns.push(column);
                }
            }
        }
        let out = {};
        if (leftPinnableColumns.length > 0) {
            out["left"] = [];
        }
        for (let i = 0; i < leftPinnableColumns.length; i++) {
            out["left"].push(leftPinnableColumns[i].field);
        }
        if (rightPinnableColumns.length > 0) {
            out["right"] = [];
        }
        for (let i = 0; i < rightPinnableColumns.length; i++) {
            out["right"].push(rightPinnableColumns[i].field);
        }
        return out;

    };

    getPinnedColumns = (breakpoint) => {
        if (breakpoint === undefined) {
            breakpoint = "xl";
        }

        let columns = {};

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getPinnedColumns("sm");
            } else {
                columns = this._getPinnedColumns(breakpoint);
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getPinnedColumns("md");
            } else {
                columns = this._getPinnedColumns(breakpoint);
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getPinnedColumns("lg");
            } else {
                columns = this._getPinnedColumns(breakpoint);
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getPinnedColumns("xl");
            } else {
                columns = this._getPinnedColumns(breakpoint);
            }
        } else if (breakpoint === "xl") {
            columns = this._getPinnedColumns(breakpoint);
        }

        return columns;
    };

    _getRowCells = (rowIndex, breakpoint) => {
        let cells = [];

        for (let columnIndex = 0; columnIndex < this.props.placement[breakpoint].length; columnIndex++) {
            let cellRenderer = this.props.columns.find((element) => element.id === this.props.placement[breakpoint][columnIndex].id).cellRenderer;
            cells.push(this.getCellRenderer(cellRenderer)(this, rowIndex, this.props.placement[breakpoint][columnIndex].id, breakpoint));
        }

        return cells;
    };

    getRowCells = (rowIndex, breakpoint) => {
        let cells = [];

        if (breakpoint === "xs") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getRowCells(rowIndex, "sm");
            } else {
                cells = this._getRowCells(rowIndex, breakpoint);
            }
        } else if (breakpoint === "sm") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getRowCells(rowIndex, "md");
            } else {
                cells = this._getRowCells(rowIndex, breakpoint);
            }
        } else if (breakpoint === "md") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getRowCells(rowIndex, "lg");
            } else {
                cells = this._getRowCells(rowIndex, breakpoint);
            }
        } else if (breakpoint === "lg") {
            if (this.props.placement[breakpoint] === undefined) {
                return this.getRowCells(rowIndex, "xl");
            } else {
                cells = this._getRowCells(rowIndex, breakpoint);
            }
        } else if (breakpoint === "xl") {
            cells = this._getRowCells(rowIndex, breakpoint);
        }

        return cells;
    };

    getRows = (breakpoint, columns) => {
        let rows = [];

        for (let i = 0; i < this.state.buffer.length; i++) {
            let row = {
                id: i,
            };
            row["RowSelected"] = i === this.getSelectedRecordNumber();
            for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                let columnMetadata = this.props.columns.find((element) => element.id === columns[columnIndex].field);

                row[columnMetadata.id] = this.state.buffer[i].get(columnMetadata.id);
            }
            rows.push(row);
        }
        return rows;
    };

    getLoadingRows = (breakpoint, columns) => {
        let rows = [];

        for (let i = 0; i < this.state.previousBuffer.length; i++) {
            let row = {
                id: i,
            };
            row["RowSelected"] = i === this.getSelectedRecordNumber();
            for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                let columnMetadata = this.props.columns.find((element) => element.id === columns[columnIndex].field);

                if (columnMetadata !== undefined) {
                    row[columnMetadata.id] = this.state.previousBuffer[i].get(columnMetadata.id);
                }
            }
            rows.push(row);
        }
        return rows;
    };

    getSearchSpecification = () => {
        return this.searchSpecification;
    };

    getSelectedRecordNumber = () => {
        return this.applet.getEnterpriseComponent().getSelectedRecordNumber();
    };

    getSortSelections = () => {
        if (this.sortSelections === null) return [];

        return this.sortSelections;
    };

    getSortSpecification = () => {
        return this.sortSpecification;
    };

    handleSortMenuClick = (sort) => {
        this.setState({sortMenuTarget: null, activeSort: sort});

        this.setSortSpecification(sort);
        this.rememberSort(sort);

        this.applet.getEnterpriseComponent().newQuery();
        this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
        if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
        this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility().Mode);
        this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility().Value);
        this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
    };

    handleSortDirectionChange = (field) => {
        let newSort = structuredClone(this.state.activeSort);

        for (let i = 0; i < newSort.Options.length; i++) {
            let option = newSort.Options[i];
            if (option.SortAttribute === field) {
                option.Direction = option.Direction === "ASC" ? "DESC" : "ASC";
            }
        }
        this.setState({activeSort: newSort});

        this.setSortSpecification(newSort);
        this.rememberSort(newSort);

        this.applet.getEnterpriseComponent().newQuery();
        this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
        if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
        this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility().Mode);
        this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility().Value);
        this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
    }

    handleMenuClick(identifier) {
        if (identifier === "Refresh") {
            this.applet.getEnterpriseComponent().invalidate();
        }
    };

    handlePageChange = (event, value) => {
        this.memoryState.pageToDisplay = value;
        if (this.state.queryController.serialNumber === this.applet.getEnterpriseComponent().serialNumber) {
            this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
        } else if (this.state.queryController.serialNumber === this.searchEnterpriseComponent.serialNumber) {
            this.searchEnterpriseComponent.executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
        }
    };

    onPageSizeHandler = (event) => {
        event.preventDefault();

        this.memoryState.pageSize = event.target.value;

        this.query();
    };

    onRowClick(params) {
        this.applet.getEnterpriseComponent().selectRecord(params.row.id);
    };

    onRowDoubleClick(params) {
        this.applet.getEnterpriseComponent().selectRecord(params.row.id);
    };

    onSearchCancelHandler = () =>  {
        this.setState({showSearch: false});
    };

    onSearchResetHandler = () =>  {
        this.searchEnterpriseComponent.newQuery();

        this.applet.getEnterpriseComponent().newQuery();
        this.applet.getEnterpriseComponent().setSortSpecification(this.getSortSpecification()?.Options);
        if (this.searchSpecification) this.applet.getEnterpriseComponent().setSearchSpecification(this.searchSpecification);
        this.applet.getEnterpriseComponent().setVisibilityMode(this.props.appContext.sessionContext.getActiveVisibility()?.Mode);
        this.applet.getEnterpriseComponent().setVisibilityValue(this.props.appContext.sessionContext.getActiveVisibility()?.Value);
        this.applet.getEnterpriseComponent().executeQuery(this.memoryState.pageToDisplay, this.memoryState.pageSize);
    };

    openRow = (event, rowIndex) => {
        event.preventDefault();
        event.stopPropagation();

        let buffer = this.state.buffer;
        let status = buffer[rowIndex].openRow;
        if (status === undefined) status = false;

        buffer[rowIndex].openRow = !status;

        this.setState({openRow: !this.state.openRow});
    };

    query = (searchSpecification) => {
        this.setSearchSpecification(searchSpecification);

        this.applet.getEnterpriseComponent().invalidate();
    };

    renderLoadingTable = () => {
        let columns = this.getColumns(this.props.breakpoint?.getBreakPointName(), true);
        let pinnedColumns = this.getPinnedColumns(this.props.breakpoint?.getBreakPointName());
        let loadingRows = this.getLoadingRows(this.props.breakpoint?.getBreakPointName(), columns);

        let rowHeight = this.props.rowHeight;
        if (rowHeight === "auto") {
            return(
                <DataGridPro style={{border: "1px solid #D8DDE4"}}
                             columns={columns}
                             pinnedColumns={pinnedColumns}
                             columnVisibilityModel={this.state.columnVisibilityModel}
                             rows={loadingRows}
                             fullWidth={true}
                             density="compact"
                             filterMode="client"
                             hideFooter={true}
                             hideFooterRowCount={true}
                             hideFooterPagination={true}
                             hideFooterSelectedRowCount={true}
                             loading={false}
                             getRowHeight={() => 'auto'}
                    sx={{
                        '.MuiDataGrid-columnHeaderTitleContainer': {
                            display: "inline",
                        }
                    }}/>
            )
        } else {
            return(
                <DataGridPro style={{border: "1px solid #D8DDE4"}}
                             columns={columns}
                             pinnedColumns={pinnedColumns}
                             columnVisibilityModel={this.state.columnVisibilityModel}
                             rows={loadingRows}
                             fullWidth={true}
                             density="compact"
                             filterMode="client"
                             hideFooter={true}
                             hideFooterRowCount={true}
                             hideFooterPagination={true}
                             hideFooterSelectedRowCount={true}
                             loading={false}
                             rowHeight={rowHeight}
                    sx={{
                        '.MuiDataGrid-columnHeaderTitleContainer': {
                            display: "inline",
                        }
                    }}/>
            )
        }
    }

    // " (N)" appended to the table title, where N is the EC's total row count.
    titleRowCountSuffix() {
        const ec = this.applet && this.applet.getEnterpriseComponent ? this.applet.getEnterpriseComponent() : null;
        const total = ec && ec.getTotalRowcount ? ec.getTotalRowcount() : -1;
        return total >= 0 ? ` (${total})` : "";
    }

    getPickableColumns = (columns) => columns.filter((c) => c.headerName !== undefined && c.headerName !== null && c.headerName !== "");

    renderColumnSelector = (columns) => {
        const pickable = this.getPickableColumns(columns);
        return (
            <Menu
                anchorEl={this.state.columnMenuTarget}
                open={Boolean(this.state.columnMenuTarget)}
                onClose={() => this.setState({columnMenuTarget: null})}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{
                    paper: {elevation: 0, sx: {boxShadow: "none", borderRadius: 0, border: "1px solid #A8A9AF", marginTop: "2px"}},
                    list: {dense: true, disablePadding: true, sx: {minWidth: "220px", maxHeight: "60vh", "& .MuiMenuItem-root": {minHeight: "34px"}}},
                }}
            >
                {pickable.map((col) => {
                    const required = col.hideable === false;
                    const visible = this.state.columnVisibilityModel[col.field] !== false;
                    return (
                        <MenuItem key={col.field} disabled={required}
                            onClick={() => { if (!required) this.setColumnVisibility(col.field, !visible); }}>
                            <ListItemIcon sx={{minWidth: "32px"}}>
                                <Checkbox edge="start" size="small" disableRipple tabIndex={-1}
                                    checked={required || visible} disabled={required} sx={{padding: "2px"}}/>
                            </ListItemIcon>
                            <ListItemText primary={col.headerName} secondary={required ? "required" : null}
                                primaryTypographyProps={{fontSize: "14px", color: "#808080"}}
                                secondaryTypographyProps={{fontSize: "11px", color: "#A8A9AF"}}/>
                        </MenuItem>
                    );
                })}
            </Menu>
        );
    };

    render() {
        let loading = this.state.fetchBackgroundOperation === true;
        let columns = this.getColumns(this.props.breakpoint?.getBreakPointName(), false);
        let pinnedColumns = this.getPinnedColumns(this.props.breakpoint?.getBreakPointName());
        let rows = loading ? [] : this.getRows(this.props.breakpoint?.getBreakPointName(), columns);

        return (
            <>
                {this.renderExtension()}
                <div key={1} style={{display: "flex", flexDirection: "column", width: "100%"}}>
                    <div style={{display: "flex", flexDirection: "row", width: "100%", height: "30px", marginLeft: "auto", marginRight: "auto", justifyContent: "space-between"}}>
                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                            {this.props.title !== null ? (
                                <div style={{fontSize: "14pt", fontWeight: "500", color: "#1498D8", margin: "-4px 0 0 0", overflow: "hidden"}}>
                                    <Typography style={{fontSize: 22, fontWeight: 500, color: "#1498D8"}}>{this.props.title}<span style={{fontWeight: 400, color: Grey5}}>{this.titleRowCountSuffix()}</span></Typography>
                                </div>
                            ): null}
                            {this.props.menu !== null ? (
                                <div style={{fontSize: "14pt", fontWeight: "500", color: "#1498D8", margin: "-3px 0 0 0", overflow: "hidden"}}>
                                    {this.renderMenu()}
                                </div>
                            ): null}
                        </div>
                        <div>
                            {loading ? (
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                                    <div style={{margin: "-3px 0 0 0"}}>
                                        <img width="35" height="35" src={spinner} alt="spinner" />
                                    </div>
                                    <div className={css_cres_table.BlinkingText} style={{margin: "4px 0 0 5px"}}>
                                         <Typography style={{fontSize: "14px", fontWeight: 400, color: Grey4}}>Loading...</Typography>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-end"}}>
                            {this.props.enableColumnSelection !== false && this.getPickableColumns(columns).some((c) => c.hideable !== false) ? (
                                <div style={{marginTop: "-9px"}}>
                                    <Tooltip title={"Columns"}>
                                        <IconButton disableRipple={true} onClick={(event) => this.setState({columnMenuTarget: event.currentTarget})}>
                                            <ViewColumnOutlined />
                                        </IconButton>
                                    </Tooltip>
                                    {this.renderColumnSelector(columns)}
                                </div>
                            ): null}
                            {this.props.sort && (this.props.enableSort === undefined || this.props.enableSort === true) ? (
                                <div style={{marginTop: "-9px"}}>
                                    <Tooltip title={"Sort"}>
                                        <IconButton disableRipple={true} onClick={(event) => {
                                            this.setState({sortMenuTarget: event.currentTarget});
                                            //this.setState({showSortSelection: true})
                                        }}>
                                            <Sort />
                                        </IconButton>
                                    </Tooltip>
                                    {this.renderSortSelector()}
                                </div>
                            ): null}
                            {(this.props.enableSearch === undefined || this.props.enableSearch === true) && this.props.simpleSearch ? (
                                <div style={{marginTop: "-9px"}}>
                                    <Tooltip title={"Search"}>
                                        <IconButton disableRipple={true}
                                            onClick={() => {
                                                if (this.state.showSearch) {
                                                    this.applet.getEnterpriseComponent().executeQueryRollback()
                                                    this.setState({showSearch: false});
                                                } else {
                                                    //this.applet.getEnterpriseComponent().setMode(EnterpriseComponent.MODE_QUERY);
                                                    this.setState({showSearch: true});
                                                }
                                            }}>
                                            <Search />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {this.state.showSearch && this.props.simpleSearch ? (
                        this.renderSearch()
                    ) : null}
                    <div style={{width: "100%"}}>
                        {loading ? this.renderLoadingTable() : (
                            <DataGridPro style={{border: "1px solid #D8DDE4", borderRadius: this.state.showSearch ? "0 0 5px 5px" : "5px 5px 5px 5px"}}
                                         columns={columns}
                                         pinnedColumns={pinnedColumns}
                                         columnVisibilityModel={this.state.columnVisibilityModel}
                                         onColumnVisibilityModelChange={this.onColumnVisibilityModelChange}
                                         rows={rows}
                                         fullWidth={true}
                                         density="compact"
                                         filterMode="client"
                                         hideFooter={true}
                                         hideFooterRowCount={true}
                                         hideFooterPagination={true}
                                         hideFooterSelectedRowCount={true}
                                         onRowDoubleClick={(params) => this.onRowDoubleClick(params)}
                                         onRowClick={(params) => this.onRowClick(params)}
                                         getRowClassName={(params) => params.row.RowSelected ? "cc-row-selected" : ""}
                                         loading={loading}
                                         {...(this.props.rowHeight ? {rowHeight: this.props.rowHeight} : {})}
                                sx={{
                                    '.MuiDataGrid-columnHeaderTitleContainer': {
                                        display: "inline",
                                    }
                                }}/>
                            )}
                    </div>
                    <div style={{display: "flex", flexDirection: "row", width: "100%", height: "30px", paddingTop: "3px", marginLeft: "auto", marginRight: "auto", justifyContent: "flex-start", background: "#FFFFFF",}}>
                        {this.applet.getEnterpriseComponent().getMode() !== EnterpriseComponent.MODE_QUERY ? this.renderPagination() : null}
                    </div>
                </div>
            </>
        );
    }

    renderEmptyHeader = (___, params, __, _) => {
        return null;
    }

    renderExtension() {
        return null;
    };

    renderAccountUserNameCell = (___, params, __, _) => {
        if (params.row.id > this.state.buffer.length - 1) return;
        if (this.state.buffer[params.row.id].get(params.field) === null) return "--";

        return (
            <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start", height: "100%"}}>
                <div key={1} style={{margin: "auto 0 auto 0"}}>
                    <Avatar sx={{bgcolor: Blue3, width: 25, height: 25, fontSize: 14, fontWeight: 500}}>{this.state.buffer[params.row.id].get(params.field).split(" ").map(word => word[0]).join("").substring(0, 2)}</Avatar>
                </div>
                <div key={2} style={{margin: "auto 0 auto 7px"}}>
                    <Typography style={{fontSize: "14px", fontWeight: 400, color: "#000000"}}>{this.state.buffer[params.row.id].get(params.field)}</Typography>
                </div>
            </div>
        );
    };

    renderLocalDateCell = (___, params, __, _) => {
        let buffer;
        let color;
        if (this.state.fetchBackgroundOperation) {
            color = "#7B8291"
            buffer = this.state.previousBuffer
        } else {
            color = Black
            buffer = this.state.buffer
        }

        if (params.row.id > buffer.length - 1) return;

        let content

        if (buffer[params.row.id].get(params.field) === null) content = "--";
        else content = formatLocalDate(buffer[params.row.id].get(params.field))

        return <div style={{whiteSpace: "nowrap", width: "100%", display: "block", color: color, height: "100%", overflow: "hidden", textOverflow: "ellipsis", alignContent: "center"}}><Typography style={{fontSize: "14px"}}>{content}</Typography></div>
    };

    renderLocalDatetimeCell = (___, params, __, _) => {
        let buffer;
        let color;
        if (this.state.fetchBackgroundOperation) {
            color = "#7B8291"
            buffer = this.state.previousBuffer
        } else {
            color = Black
            buffer = this.state.buffer
        }

        if (params.row.id > buffer.length - 1) return;

        let content

        if (buffer[params.row.id].get(params.field) === null) content = "--";
        else content = formatLocal(buffer[params.row.id].get(params.field))

        return <div style={{whiteSpace: "nowrap", width: "100%", display: "block", color: color, height: "100%", overflow: "hidden", textOverflow: "ellipsis", alignContent: "center"}}><Typography style={{fontSize: "14px"}}>{content}</Typography></div>
    };

    renderMenuContent() {
        let menuItems = [];

        let elementConfig = {
            options: [],
        };
        for (let i = 0; i < this.props.menu.options.length; i++) {
            let requiredPrivileges = this.props.menu.options[i].privileges;
            let option = {
                displayValue: this.props.menu.options[i].displayValue,
                value: this.props.menu.options[i].value,
                active: this.shouldRenderMenuItem(this.props.menu.options[i].value)
            };
            if (requiredPrivileges === undefined) {
                elementConfig.options.push(option);
            } else {
                let assignedPrivileges = this.props.appContext.sessionContext.getPrivileges();
                let permissionFound = false;
                for (let i = 0; i < requiredPrivileges.length; i++) {
                    for (let j = 0; j < assignedPrivileges.length; j++) {
                        if (requiredPrivileges[i] === assignedPrivileges[j]) {
                            elementConfig.options.push(option);
                            permissionFound = true;
                        }
                    }
                }
                if (!permissionFound) {
                    option.active = false;
                    elementConfig.options.push(option);
                }
            }
        }

        for (let i = 0; i < elementConfig.options.length; i++) {
            menuItems.push(
                <MenuItem disabled={!elementConfig.options[i].active} key={"menuItem" + i} onClick={(e) => {
                    e.stopPropagation();
                    this.handleMenuClick(elementConfig.options[i].value);
                    this.setState({showMenu: false});
                }}>
                    {elementConfig.options[i].displayValue}
                </MenuItem>,
            );
        }

        return menuItems;
    }

    renderMenu() {
        let menu = null;
        let content = null;

        if (this.props.menu && this.props.menu.options.length !== 0) {
            let menuItems = this.renderMenuContent();

            menu = (
                <Menu key={"menu"} id="tableMenu" anchorEl={this.state.menuTarget} open={true} onClose={() => this.setState({menuTarget: null, showMenu: false})} elevation={2}>
                    {menuItems}
                </Menu>
            );
            if (this.state.showMenu === false) menu = null;

            content = (
                <>
                    <Button className={css_cres_table.MenuButton} style={{marginLeft: "-5px", marginTop: "0px"}} variant="text"
                            onClick={(event) => {
                                event.stopPropagation();
                                this.setState({menuTarget: event.currentTarget, showMenu: true});
                            }}
                            endIcon={<MoreVert sx={{width: "25px", height: "25px"}} />}>
                    </Button>
                    {menu}
                </>
            );
        }
        return content;
    };

    renderPagination = () => {
        let pageSizeSelector = (
            <div style={{display: "flex", alignItems: "center", marginLeft: "20px", marginTop: "-5px"}}>
                <Typography component="div" style={{fontSize: "13px", color: "#7b8291"}}>Results per page:</Typography>
                <TextField
                    select={true}
                    key="1"
                    margin="dense"
                    id="PageSizeSelector"
                    onChange={(event) => this.onPageSizeHandler(event)}
                    value={this.memoryState.pageSize}
                    variant={"standard"}
                    slotProps={{
                        input: {},

                        select: {
                            MenuProps: {
                                PaperProps: {
                                    elevation: 1,
                                },
                            },
                            className: css_cres_table.SelectPage,
                            IconComponent: ExpandMore,
                        },

                        inputLabel: {shrink: true}
                    }}>
                    <MenuItem key={1} value={5}>{5}</MenuItem>
                    <MenuItem key={2} value={10}>{10}</MenuItem>
                    <MenuItem key={3} value={20}>{20}</MenuItem>
                    <MenuItem key={4} value={40}>{40}</MenuItem>
                </TextField>
            </div>
        );

        let pagination = null;
        if (this.applet.getEnterpriseComponent().getTotalRowcount() > 0) {
            let recordDisplayString;
            if (this.state.buffer.length === 0) {
                recordDisplayString = "0 of 0";
            } else {
                let firstRecordOnDisplay = (this.memoryState.pageToDisplay - 1) * this.memoryState.pageSize + 1;
                let lastRecordOnDisplay = firstRecordOnDisplay + this.memoryState.pageSize - 1;
                if (lastRecordOnDisplay >= this.applet.getEnterpriseComponent().getTotalRowcount()) {
                    lastRecordOnDisplay = this.applet.getEnterpriseComponent().getTotalRowcount();
                }
                if (this.applet.isRowcountKnown() === true) {
                    if (lastRecordOnDisplay > this.applet.getEnterpriseComponent().getTotalRowcount()) {
                        lastRecordOnDisplay = this.applet.getEnterpriseComponent().getTotalRowcount();
                    }
                    recordDisplayString = firstRecordOnDisplay + " - " + lastRecordOnDisplay + " of " + this.applet.getEnterpriseComponent().getTotalRowcount();
                } else {
                    if (lastRecordOnDisplay > this.state.buffer.length) {
                        lastRecordOnDisplay = this.state.buffer.length;
                    }
                    recordDisplayString = firstRecordOnDisplay + " - " + lastRecordOnDisplay + " of " + this.state.buffer.length;
                }
            }

            let recordDisplay = (
                <div style={{marginTop: "2px"}}>
                    <div>
                        <span>{recordDisplayString}</span>
                    </div>
                </div>
            );

            pagination = (
                <React.Fragment>
                    {pageSizeSelector}
                    <div style={{width: "20px"}} />
                    <Pagination color="primary"
                                count={this.memoryState.numberOfPages}
                                defaultPage={1}
                                page={this.memoryState.pageToDisplay}
                                size="small"
                                boundaryCount={2}
                                showFirstButton
                                showLastButton
                                shape="rounded"
                                onChange={(object, page) => this.handlePageChange(object, page)} />
                    <div style={{width: "20px"}} />
                    <div style={{marginTop: "2px"}}>
                        <Typography component="div" style={{fontSize: "13px", color: "#7b8291"}}>{recordDisplay}</Typography>
                    </div>
                </React.Fragment>
            );
        }
        return pagination;
    };

    shouldRenderMenuItem(identifier) {
        return true;
    };

    renderRowSelectionIndicatorCell = (___, params, __, _) => {
        if (params.row.id > this.state.buffer.length - 1) return;

        if (this.state.buffer.length === 0 || params.row.id === this.state.buffer.length || this.state.buffer[params.row.id].get("RowSelected") !== true) return;
        return (
            <div style={{display: "flex", flexDirection: "column", justifyContent: "center", height: "100%"}}>
                <ArrowRight/>
            </div>
        );
    };

    renderSearch() {
        MemoryContext.components["SimpleSearchFormApplet-PageSize"] = this.memoryState.pageSize;
        return (
            <div style={{border: "1px solid #D8DDE4", borderWidth: "1px 1px 0px 1px", borderRadius: "5px 5px 0 0", height: "auto"}}>
                <div style={{display: "flex", flexDirection: "column", padding: "10px 20px 10px 20px"}}>
                    <SimpleSearchFormApplet enterpriseComponent={this.searchEnterpriseComponent} controls={this.props.simpleSearch.Controls} actions={this.props.simpleSearch.Actions} defaultAction={"Query"}/>
                </div>
            </div>
        );
    };

    renderSortSelector = () => {
        let menu = null;
        let menuItems = [];

        for (let i = 0; i < this.getSortSelections().length; i++) {
            let sortSelection = this.getSortSelections()[i];
            if (sortSelection.Identifier === undefined) continue;
            if (sortSelection.Options === undefined) continue;
            // VISIBLE IS HONOURED HERE, and was honoured nowhere. Every selection carries the flag
            // and this loop rendered all of them regardless, so the one entry that sets it false --
            // the framework's own "Creation Datetime" fallback, which orders by a RowIdentity most
            // IDOCs do not expose -- appeared in every grid's menu beside the grid's own sorts.
            //
            // Hidden means hidden from the MENU, not disabled: an invisible selection is still the
            // default ordering for a grid that declares none of its own, which is the case the
            // fallback exists for.
            if (sortSelection.Visible === false) continue;

            let sortSelectionOptions = sortSelection.Options;

            let sortContent = [];
            for (let j = 0; j < sortSelectionOptions.length; j++) {
                let sortSelectionOption = sortSelectionOptions[j];
                sortContent.push(sortSelectionOption.DisplayName);
            }
            let sortContentString = sortContent.join(" | ");

            menuItems.push(
                <MenuItem key={"sortMenuItem" + i} onClick={() => {
                    this.handleSortMenuClick(sortSelection);
                }}>
                    <ListItemIcon>
                        {this.state.activeSort.Identifier === sortSelection.Identifier ? <Check /> : null}
                    </ListItemIcon>
                    <ListItemText primary={sortContentString} />
                </MenuItem>,
            );
        }

        menu = (
            <Menu id="sortMenu" anchorEl={this.state.sortMenuTarget} open={true} onClose={() => this.setState({sortMenuTarget: null})} elevation={2}>
                {menuItems}
            </Menu>
        );
        if (this.state.sortMenuTarget === null) {
            menu = null;
        }
        return menu;
    };

    renderStandardSingleCell = (___, params, __, _) => {
        let buffer;
        let color;
        if (this.state.fetchBackgroundOperation) {
            color = "#7B8291"
            buffer = this.state.previousBuffer
        } else {
            color = Black
            buffer = this.state.buffer
        }

        if (params.row.id > buffer.length - 1) return;

        return (
            <div>
                <div className={css_cres_table.UnderlineOnHover} style={{whiteSpace: "nowrap", width: "100%", display: "block", fontSize: 14, color: color, height: "100%", overflow: "hidden", textOverflow: "ellipsis"}}>{buffer[params.row.id].get(params.field) ?? "--"}</div>
            </div>
        );
    };

    renderPathCell = (___, params, __, _) => {
        return (
            <div>
                &nbsp;
            </div>
        );
    };

    renderStandardSingleHeader = (___, params, __, _) => {
        let content;

        let sortContent;
        let align;

        if (params.colDef.align === "left") align = "flex-start";
        else if (params.colDef.align === "right") align = "flex-end";
        else if (params.colDef.align === "center") align = "center";

        content = (
            <div style={{width: "100%", paddingTop: "10px", height: "37px", fontSize: 14, color: Black}}>
                <div style={{display: "flex", flexDirection: "row", alignContent: align}}><b>{params.colDef.headerName}</b></div>
            </div>
        )
        if (this.state.activeSort) {
            let sortSelectionOptions = this.state.activeSort.Options;
            for (let j = 0; j < sortSelectionOptions.length; j++) {
                let sortSelectionOption = sortSelectionOptions[j];
                if (sortSelectionOption.SortAttribute === params.colDef.field) {
                    let sortDirection = sortSelectionOption.Direction;

                    sortContent = (
                        <>
                            <Tooltip title={sortSelectionOption.Direction === "DESC" ? "ASC" : "DESC"}>
                                <div style={{display: "flex", flexDirection: "row", justifyContent: "flex-start"}}>
                                    <IconButton className={css_button.sortIconButton} onClick={() => this.handleSortDirectionChange(params.colDef.field)}>
                                        {sortDirection === "DESC" ? <North sx={{width: 15, height: 15}}/> : <South sx={{width: 15, height: 15}}/>}
                                    </IconButton>
                                    <Typography style={{position: "relative", fontSize: "9px", top: "-3px"}}>{j + 1}</Typography>
                                </div>
                            </Tooltip>
                        </>
                    )

                    content = (
                        <div style={{width: "100%", paddingTop: "8px", height: "37px", fontSize: 14, color: Black}}>
                            <div style={{display: "flex", flexDirection: "row", justifyContent: "space-between"}}>
                                <div style={{display: "flex", flexDirection: "row", alignContent: align, marginTop: "2px"}}><b>{params.colDef.headerName}</b></div>
                                <div style={{display: "flex", flexDirection: "row", alignContent: "flex-end", marginTop: "3px"}}>
                                    {sortContent}
                                </div>
                            </div>
                        </div>
                    )
                }
            }
        }

        return (
            content
        );
    };

    renderTimingCell = (___, params, __, _) => {
        return (
            <div style={{display: "flex", flexDirection: "column", justifyContent: "flex-start"}}>
                <div><span style={{color: "#EEEEEE"}}>Created Datetime:</span></div>
                <div>{this.state.buffer[params.row.id]["CreatedDatetime"]}</div>
                <div><span style={{color: "#EEEEEE"}}>End Datetime:</span></div>
                <div>{this.state.buffer[params.row.id]["EndDatetime"]}</div>
            </div>
        );
    };

    setSearchSpecification = (searchSpecification) => {
        this.searchSpecification = searchSpecification;
    };

    setSortSelections = (sortSelections) => {
        this.sortSelections = sortSelections;
    };

    setSortSpecification = (sortSpecification) => {
        this.sortSpecification = sortSpecification;
    };

}

export default withBreakpoint(withAppContext(PlatformTable2));